import { Loader2, TriangleAlert, type LucideIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { errorMessage } from '@/lib/api';
import { FLOW, STATUS, isException } from '@/lib/status';
import type { OrderStatus } from '@/lib/types';
import { Button } from './Button';

export function Spinner({ className, label = 'Loading' }: { className?: string; label?: string }) {
  return <Loader2 role="status" aria-label={label} className={clsx('h-5 w-5 animate-spin text-brand-600', className)} />;
}

export function PageLoader({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 text-sm text-slate-500">
      <Spinner className="h-7 w-7" />
      {label}
    </div>
  );
}

export function Skeleton({ className }: { className?: string }) {
  return <div className={clsx('animate-pulse rounded-lg bg-slate-200/70 dark:bg-ink-800', className)} aria-hidden />;
}

export function EmptyState({ icon: Icon, title, text, action }: { icon: LucideIcon; title: string; text?: ReactNode; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:bg-ink-800 dark:text-slate-500">
        <Icon className="h-6 w-6" aria-hidden />
      </div>
      <p className="font-medium text-slate-900 dark:text-white">{title}</p>
      {text && <p className="mt-1 max-w-sm text-sm text-slate-500 dark:text-slate-400">{text}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  return (
    <div role="alert" className="flex flex-col items-center justify-center px-6 py-12 text-center">
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-rose-50 text-rose-500 dark:bg-rose-500/10">
        <TriangleAlert className="h-6 w-6" aria-hidden />
      </div>
      <p className="font-medium text-slate-900 dark:text-white">We couldn't load this</p>
      <p className="mt-1 max-w-sm text-sm text-slate-500">{errorMessage(error)}</p>
      {onRetry && (
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

const statTones = {
  brand: 'bg-brand-50 text-brand-600 dark:bg-brand-500/15 dark:text-brand-300',
  green: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-300',
  amber: 'bg-amber-50 text-amber-600 dark:bg-amber-500/15 dark:text-amber-300',
  red: 'bg-rose-50 text-rose-600 dark:bg-rose-500/15 dark:text-rose-300',
  teal: 'bg-accent-50 text-accent-600 dark:bg-accent-500/15 dark:text-accent-300',
  slate: 'bg-slate-100 text-slate-600 dark:bg-ink-800 dark:text-slate-300',
};

export function Stat({ label, value, sub, icon: Icon, tone = 'brand' }: { label: string; value: ReactNode; sub?: ReactNode; icon: LucideIcon; tone?: keyof typeof statTones }) {
  return (
    <div className="card p-4 sm:p-5">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm font-medium text-slate-500 dark:text-slate-400">{label}</p>
        <span className={clsx('flex h-9 w-9 shrink-0 items-center justify-center rounded-lg', statTones[tone])}>
          <Icon className="h-[18px] w-[18px]" aria-hidden />
        </span>
      </div>
      <p className="mt-2 text-2xl font-bold tracking-tight text-slate-900 dark:text-white">{value}</p>
      {sub && <p className="mt-1 text-xs text-slate-500">{sub}</p>}
    </div>
  );
}

/** Segmented progress through the seven lifecycle states. */
export function OrderProgress({ status, compact }: { status: OrderStatus; compact?: boolean }) {
  const steps = FLOW.slice(1);
  const current = STATUS[status].step;
  const exception = isException(status);
  return (
    <ol className={clsx('flex items-center', compact ? 'gap-1' : 'gap-1.5')} aria-label={`Progress: ${STATUS[status].label}`}>
      {steps.map((s) => {
        const done = !exception && STATUS[s].step <= current;
        return <li key={s} title={STATUS[s].label} className={clsx('h-1.5 flex-1 rounded-full', done ? STATUS[status].dot : 'bg-slate-200 dark:bg-ink-700')} />;
      })}
    </ol>
  );
}

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: ReactNode; actions?: ReactNode }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
      <div>
        <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{subtitle}</p>}
      </div>
      {actions}
    </div>
  );
}

export function SectionCard({ title, subtitle, actions, children, className, bodyClass }: { title?: ReactNode; subtitle?: ReactNode; actions?: ReactNode; children: ReactNode; className?: string; bodyClass?: string }) {
  return (
    <section className={clsx('card', className)}>
      {(title || actions) && (
        <header className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-4" style={{ borderColor: 'var(--border)' }}>
          <div>
            {title && <h2 className="font-semibold text-slate-900 dark:text-white">{title}</h2>}
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
          {actions}
        </header>
      )}
      <div className={bodyClass}>{children}</div>
    </section>
  );
}

export function Dl({ items, className }: { items: [string, ReactNode][]; className?: string }) {
  return (
    <dl className={clsx('grid gap-x-4 gap-y-3 text-sm sm:grid-cols-2', className)}>
      {items.map(([k, v]) => (
        <div key={k}>
          <dt className="text-xs text-slate-500">{k}</dt>
          <dd className="break-words text-slate-900 dark:text-white">{v ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}
